import React from 'react';
import { string, bool, number } from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';
import {
  params as queryParams,
  arrayOf as arrayType,
  shape as shapeType,
  string as stringType,
  number as numberType,
  useDataModel
} from '@thd-nucleus/data-sources';

import { getStoreDetailStructureData } from './structured-data';
import { getGraphQlReviewsOptions } from './helpers';
import { PAGE_TYPE } from '../constants';

const HD_URL = 'https://www.homedepot.com';
const OG_IMAGE = 'https://assets.thdstatic.com/mcstore/images/hero-200x155.jpg';

const getStoreDetails = (data) => {
  const { name, storeDetailsPageLink } = data?.storeSearch?.stores[0] || '';
  const { city, state, postalCode } = data?.storeSearch?.stores[0]?.address || {};

  return {
    name, city, state, postalCode, storeDetailsPageLink
  };
};

const getStoreDetailsTitle = ({
  city,
  name,
  postalCode,
  seoPageNumber,
  isReviewPage,
  state
}) => {
  const hasRequiredPostfixData = !!city && !!state && !!postalCode;
  const titlePrefix = 'The Home Depot';
  const storeName = name || '';
  let title = '';
  if (isReviewPage) {
    title = `${storeName} Home Depot Store Reviews, Pg ${seoPageNumber || ''}`
    + `${hasRequiredPostfixData ? ` | ${city}, ${state} ${postalCode}` : ''}`;
  } else {
    title = `${titlePrefix} ${storeName}`
  + `${hasRequiredPostfixData ? ` Store in ${city}, ${state} ${postalCode}` : ''}`;
  }
  return title;
};

const getMetaDescription = ({
  city, name, isReviewPage, state
}) => {
  const hasRequiredDescriptionData = !!city && !!state && !!name;
  const descriptionPostfix = `${name} store in ${city}, ${state}.`;

  if (hasRequiredDescriptionData && !isReviewPage) {
    return 'Save time on your trip to the Home Depot by scheduling your order with buy online '
      + `pick up in store or schedule a delivery directly from your ${descriptionPostfix}`;
  }

  if (isReviewPage) {
    return `See what shoppers are saying about their experience visiting The Home Depot ${descriptionPostfix}`;
  }

  return 'The Home Depot';
};

const getCanonicalUrl = ({ isReviewPage, storeDetailsPageLink, page }) => {
  return isReviewPage
    ? `${HD_URL}${storeDetailsPageLink}/reviews/${page}`
    : `${HD_URL}${storeDetailsPageLink}`;
};

const storeDetailsHandler = ({ data, props }) => {
  const tags = [];

  const {
    name, city, state, postalCode, storeDetailsPageLink
  } = getStoreDetails(data.storeData);
  const totalReviews = data?.reviewData?.storeReviews?.TotalResults;
  const { storeId, seoPageNumber, isReviewPage, hasFaq } = props;
  const valueOptions = {
    city,
    name,
    postalCode,
    seoPageNumber,
    isReviewPage,
    state
  };

  const storeDetailsTitle = getStoreDetailsTitle(valueOptions);
  const metaDescription = getMetaDescription(valueOptions);
  const canonicalUrl = getCanonicalUrl({ isReviewPage, storeDetailsPageLink, page: seoPageNumber });
  const alternateLink = `android-app://com.thehomedepot/homedepot/storedetails/${storeId}`;

  const storeDetailStructuredLocalBusinessData = getStoreDetailStructureData(
    'LocalBusiness',
    data
  );
  const storeDetailStructuredBreadcrumb = getStoreDetailStructureData(
    'BreadcrumbList',
    data,
    isReviewPage
  );
  const storeDetailStructuredFAQData = getStoreDetailStructureData(
    'FAQPage',
    data
  );

  tags.push(new TitleTag(storeDetailsTitle));
  if (seoPageNumber > Math.ceil(totalReviews / 30)) {
    tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX', id: 'robots' }));
  }
  tags.push(new MetaTag({ name: 'description', content: metaDescription, id: 'description' }));
  tags.push(new LinkTag({
    rel: 'canonical',
    href: canonicalUrl,
    id: 'canonical'
  }));
  tags.push(new MetaTag({ property: 'og:url', content: canonicalUrl, id: 'canonicalURL' }));

  if (!isReviewPage) {
    tags.push(new LinkTag({ rel: 'alternate', href: alternateLink, id: 'alternate' }));
    tags.push(new MetaTag({ property: 'og:type', content: 'PPS Main Store', id: 'ogType' }));
  } else {
    tags.push(new MetaTag({ property: 'og:type', content: 'PPS Review', id: 'ogType' }));
  }
  tags.push(new MetaTag({ property: 'og:title', content: storeDetailsTitle, id: 'ogTitle' }));
  tags.push(new MetaTag({ property: 'og:description', content: metaDescription, id: 'ogDescription' }));
  tags.push(new MetaTag({ property: 'og:image', content: OG_IMAGE, id: 'ogImage' }));
  tags.push(new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'og-site_name' }));
  tags.push(new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fb-admins' }));
  tags.push(new ScriptTag({
    content: storeDetailStructuredBreadcrumb,
    id: 'storeDetailStructuredBreadcrumb'
  }));
  tags.push(new ScriptTag({
    content: storeDetailStructuredLocalBusinessData,
    id: 'storeDetailStructuredLocalBusinessData'
  }));
  if (hasFaq) {
    tags.push(new ScriptTag({
      content: storeDetailStructuredFAQData,
      id: 'storeDetailStructuredFAQData'
    }));
  }
  return tags;
};

const StoreDetailMetadata = ({
  storeId, isReviewPage, seoPageNumber, hasFaq, pageSize
}) => {
  const pageType = isReviewPage ? PAGE_TYPE.REVIEWS : PAGE_TYPE.DETAILS;
  const options = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };
  const faqOption = {
    variables: {
      storeId,
    }
  };
  const reviewsOption = getGraphQlReviewsOptions({ pageType, pageSize, storeId, page: seoPageNumber });

  const { data, loading, error } = useDataModel('storeSearch', options);
  const { data: faqData, loading: faqLoading, error: faqError } = useDataModel('seoStoreDetails', faqOption);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('storeReviews', reviewsOption);

  const deps = data?.storeSearch?.stores[0]?.address && faqData?.seoStoreDetails?.[1] && reviewData;
  const helmetData = {
    storeData: data,
    faqData,
    reviewData
  };
  const props = {
    storeId,
    seoPageNumber,
    hasFaq,
    isReviewPage
  };
  useHelmet('storeDetailMetadata', { data: helmetData, props }, storeDetailsHandler, [deps]);
  return null;
};

StoreDetailMetadata.displayName = 'StoreDetailMetadata';

StoreDetailMetadata.dataModel = {
  storeSearch: queryParams({
    storeSearchInput: stringType().isRequired(),
    pagesize: stringType().isRequired()
  }).shape({
    stores: arrayType(shapeType({
      address: shapeType({
        city: stringType(),
        state: stringType(),
        postalCode: stringType(),
        street: stringType()
      }),
      storeId: stringType(),
      name: stringType(),
      phone: stringType(),
      storeDetailsPageLink: stringType(),
      storeHours: shapeType({
        sunday: shapeType({
          open: stringType(),
          close: stringType()
        }),
        monday: shapeType({
          open: stringType(),
          close: stringType()
        })
      })
    }))
  }),
  seoStoreDetails: queryParams({ storeId: stringType().isRequired() }).arrayOf(shapeType({
    faq: arrayType(shapeType({
      question: stringType(),
      answer: stringType()
    }))
  })),
  storeReviews: queryParams({ storeId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    })),
    Includes: shapeType({
      Products: shapeType({
        store: shapeType({
          FilteredReviewStatistics: shapeType({
            AverageOverallRating: stringType(),
            TotalReviewCount: numberType()
          })
        })
      })
    })
  })
};

StoreDetailMetadata.propTypes = {
  storeId: string,
  isReviewPage: bool,
  seoPageNumber: string,
  hasFaq: bool,
  pageSize: number
};

StoreDetailMetadata.defaultProps = {
  storeId: '',
  isReviewPage: false,
  seoPageNumber: '',
  hasFaq: true,
  pageSize: 10
};

export { StoreDetailMetadata };